import {
  Component,
  EventEmitter,
  Output,
  ViewChild,
  ViewEncapsulation,
  OnInit,
  OnDestroy,
} from "@angular/core";
import { Subscription } from "rxjs";

import { AuthService } from "src/app/services/auth/auth.service";
import { UserProfile, UserService } from "src/app/services/user/user.service";
import { Router, RouterModule } from "@angular/router";
import { MatBadgeModule } from "@angular/material/badge";
import { MatButtonModule } from "@angular/material/button";
import { MatMenu, MatMenuModule } from "@angular/material/menu";
import { MaterialModule } from "src/app/material.module";
import { BrandingComponent } from "../sidebar/branding.component";
import { DefaultImagePipe } from "src/app/pipe/default-image.pipe";
import { DEFAULT_AUTHENTICATED_ROUTES } from "src/app/permissions/rbac-policy";
import { NotificationResponse } from "src/app/shared/models/notification-response.model";
import { NotificationApiService } from "src/app/services/notifications/notification-api.service";
import { NotificationRealtimeService } from "src/app/services/notifications/notification-realtime.service";
import { NotificationStoreService } from "src/app/services/notifications/notification-store.service";
import { environment } from "src/environment/environment";
import { AppThemeMode, ThemeService } from "src/app/services/theme/theme.service";

@Component({
  selector: "app-topstrip",
  templateUrl: "./topstrip.component.html",
  styleUrls: ["./topstrip.component.scss"],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    RouterModule,
    MatButtonModule,
    MatMenuModule,
    MaterialModule,
    MatBadgeModule,
    BrandingComponent,
    DefaultImagePipe
  ],
})
export class AppTopstripComponent implements OnInit, OnDestroy {
  isLoggedIn = false;
  userRole = "";
  userRoleLabel = "";
  userRoleLabels: string[] = [];
  userEmail = "";
  userDisplayName = "";
  userProfileImage = "assets/images/profile/user-1.jpg";
  isPublicPage = false;
  notifications: NotificationResponse[] = [];
  unreadCount = 0;
  cartItemsCount = 0;
  cartItems: any[] = [];
  themeMode: AppThemeMode = "light";
  currentDateTime = new Date();

  private notificationInitialized = false;
  private profileLoaded = false;
  private clockTimerId: ReturnType<typeof setInterval> | null = null;

  @Output() toggleMobileNav = new EventEmitter<void>();
  @Output() toggleSidenav = new EventEmitter<void>();
  @ViewChild("cartMenu") cartMenu!: MatMenu;

  private subs = new Subscription();

  private publicPages = ["/home", "/faq", "/contact", "/about", "/blog"];

  constructor(
    private router: Router,
    private authService: AuthService,
    private userService: UserService,
    private notificationApi: NotificationApiService,
    private notificationRealtime: NotificationRealtimeService,
    private notificationStore: NotificationStoreService,
    private themeService: ThemeService,
  ) {}

  ngOnInit() {
    this.clockTimerId = setInterval(() => {
      this.currentDateTime = new Date();
    }, 1000);

    // Watch login state
    this.subs.add(
      this.authService.authState$.subscribe((loggedIn) => {
        this.isLoggedIn = loggedIn;
        this.userRole = loggedIn ? this.authService.getUserRole() : "";
        this.userRoleLabel = loggedIn ? this.authService.getUserRoleLabel() : "";
        this.userRoleLabels = loggedIn ? this.getUniqueAccessLabels(this.authService.getUserRoleLabels()) : [];
        this.userEmail = loggedIn ? this.authService.getCurrentUserEmail() : "";
        this.userDisplayName = loggedIn ? this.getFallbackDisplayName() : "";
        if (!loggedIn) {
          this.notificationInitialized = false;
          this.profileLoaded = false;

          this.cartItems = [];
          this.cartItemsCount = 0;
          this.notifications = [];
          this.unreadCount = 0;
          this.userDisplayName = "";
          this.userProfileImage = "assets/images/profile/user-1.jpg";

          this.userService.setUserProfile(null);
          this.notificationRealtime.stop();

          return;
        }

        if (!this.profileLoaded) {
          this.profileLoaded = true;
          this.loadCurrentUserProfile();
        }

        if (!this.notificationInitialized) {
          this.notificationInitialized = true;
          this.notificationRealtime.start();
        }
      })
    );

    this.subs.add(
      this.notificationStore.notifications$.subscribe((notifications) => {
        this.notifications = notifications;
      })
    );

    this.subs.add(
      this.notificationStore.unreadCount$.subscribe((count) => {
        this.unreadCount = count;
      })
    );

    this.subs.add(
      this.userService.userProfile$.subscribe((profile) => {
        this.applyUserProfile(profile);
      })
    );

    this.subs.add(
      this.themeService.theme$.subscribe((theme) => {
        this.themeMode = theme;
      })
    );

    // Track current page
    this.subs.add(
      this.router.events.subscribe(() => {
        this.isPublicPage = this.publicPages.includes(this.router.url);
      })
    );
  }

  setThemeMode(theme: AppThemeMode): void {
    this.themeService.setTheme(theme);
  }

  get formattedHeaderDate(): string {
    return this.currentDateTime.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }

  get formattedHeaderTime(): string {
    return this.currentDateTime.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
    });
  }

  // ------------------------------
  // 🔵 DASHBOARD Redirect for roles
  // ------------------------------
  private getDashboardRoute(role: string): string {
    return DEFAULT_AUTHENTICATED_ROUTES[role as keyof typeof DEFAULT_AUTHENTICATED_ROUTES] || "/home";
  }

  private getUniqueAccessLabels(labels: string[]): string[] {
    if (this.userRole === "superadmin") {
      return [];
    }

    const normalizedPrimary = this.userRoleLabel.trim().toLowerCase();
    return labels.filter((label, index, items) => {
      const normalizedLabel = label.trim().toLowerCase();
      return normalizedLabel !== normalizedPrimary && items.findIndex(
        (item) => item.trim().toLowerCase() === normalizedLabel
      ) === index;
    });
  }

  private loadCurrentUserProfile(): void {
    this.userService.getUserDetails().subscribe({
      next: () => {},
      error: () => {
        this.userDisplayName = this.getFallbackDisplayName();
        this.userProfileImage = "assets/images/profile/user-1.jpg";
      },
    });
  }

  loadNotificationSummary(): void {
    this.notificationRealtime.refreshNow();
  }

  connectNotificationSocket(): void {
    this.notificationRealtime.start();
  }

  openNotification(notification: NotificationResponse): void {
    const navigate = () => {
      const actionUrl = this.normalizeNotificationActionUrl(notification);
      if (actionUrl) {
        void this.router.navigateByUrl(actionUrl);
      }
    };

    if (notification.read) {
      navigate();
      return;
    }

    this.notificationStore.markRead({
      ...notification,
      read: true,
      readAt: notification.readAt ?? new Date().toISOString(),
    });
    this.notificationStore.setUnreadCount(Math.max(this.notificationStore.unreadCount - 1, 0));

    this.subs.add(
      this.notificationApi.markAsRead(notification.id).subscribe({
        next: (updated) => {
          this.notificationStore.markRead({ ...updated, read: true });
        },
        error: (err) => {
          this.logDev("Failed to mark notification as read", err);
          this.loadNotificationSummary();
        },
      })
    );

    navigate();
  }

  markAllNotificationsAsRead(): void {
    if (!this.unreadCount) {
      return;
    }

    this.subs.add(
      this.notificationApi.markAllAsRead().subscribe({
        next: () => {
          this.notificationStore.markAllRead();
        },
        error: (err) => this.logDev("Failed to mark all notifications as read", err),
      })
    );
  }

  goToNotificationPage(): void {
    void this.router.navigate(["/app/notifications"]);
  }

  getNotificationIcon(type: string): string {
    switch (type) {
      case "LEAD_ASSIGNED":
      case "LEAD_STAGE_CHANGED":
        return "person_add";

      case "BOOKING_CREATED":
      case "BOOKING_UPDATED":
      case "BOOKING_CANCELLED":
        return "event";

      case "PERMISSIONS_UPDATED":
        return "admin_panel_settings";

      case "FORM_SUBMITTED":
      case "CONTACT_INQUIRY_CREATED":
        return "assignment";

      case "EMAIL_SENT":
        return "mail";

      default:
        return "notifications";
    }
  }

  formatNotificationTime(value: string): string {
    if (!value) {
      return "";
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "";
    }

    return date.toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  }

  private normalizeNotificationList(response: any): NotificationResponse[] {
    const data = response?.data ?? response;
    const notifications = data?.content ?? data?.notifications ?? data;
    return Array.isArray(notifications) ? notifications.slice(0, 20) : [];
  }

  private normalizeNotificationActionUrl(notification: NotificationResponse): string {
    const normalizedUrl = notification.actionUrl?.trim();
    const billingNotificationUrl = this.resolveBillingNotificationUrl(notification);
    if (billingNotificationUrl) {
      return billingNotificationUrl;
    }

    const adminRegistrationUrl = this.resolveAdminRegistrationNotificationUrl(notification);
    if (adminRegistrationUrl) {
      return adminRegistrationUrl;
    }

    if (!normalizedUrl) {
      return "";
    }

    const appUrl = this.toAppRelativeUrl(normalizedUrl);

    if (appUrl === "/admin/pipeline-board" || appUrl.startsWith("/admin/pipeline-board?")) {
      return appUrl.replace("/admin/pipeline-board", "/app/pipeline-board");
    }

    if (this.isBillingRequestUrl(appUrl)) {
      return "/superadmin/billing-requests";
    }

    const adminUserUrl = this.resolveAdminUserUrl(appUrl);
    if (adminUserUrl) {
      return adminUserUrl;
    }

    return appUrl;
  }

  private resolveBillingNotificationUrl(notification: NotificationResponse): string {
    const role = this.userRole.toLowerCase();
    const appUrl = notification.actionUrl?.trim()
      ? this.toAppRelativeUrl(notification.actionUrl.trim())
      : "";

    if (!this.isBillingRequestNotification(notification) && !this.isBillingRequestUrl(appUrl)) {
      return "";
    }

    if (role === "superadmin") {
      return "/superadmin/billing-requests";
    }

    return "/app/billing/subscription";
  }

  private toAppRelativeUrl(url: string): string {
    try {
      const parsed = new URL(url, window.location.origin);
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    } catch {
      return url.startsWith("/") ? url : `/${url}`;
    }
  }

  private isBillingRequestNotification(notification: NotificationResponse): boolean {
    const type = notification.type?.toUpperCase() ?? "";
    const entityType = notification.entityType?.toUpperCase() ?? "";
    const text = `${notification.title ?? ""} ${notification.message ?? ""} ${notification.metadataJson ?? ""}`.toLowerCase();

    return type.includes("BILLING") ||
      entityType.includes("BILLING") ||
      entityType.includes("SUBSCRIPTION_REQUEST") ||
      text.includes("billing request") ||
      text.includes("subscription request");
  }

  private isBillingRequestUrl(url: string): boolean {
    const normalized = url.toLowerCase();
    return normalized === "/superadmin/billing-requests" ||
      normalized.startsWith("/superadmin/billing-requests?") ||
      normalized.includes("/billing/subscription-requests") ||
      normalized.includes("/subscription-requests") ||
      normalized.includes("billing-requests");
  }

  private resolveAdminRegistrationNotificationUrl(notification: NotificationResponse): string {
    if (this.userRole.toLowerCase() !== "superadmin") {
      return "";
    }

    const appUrl = notification.actionUrl?.trim()
      ? this.toAppRelativeUrl(notification.actionUrl.trim())
      : "";

    if (!this.isAdminRegistrationNotification(notification) && !this.resolveAdminUserUrl(appUrl)) {
      return "";
    }

    const adminUserUrl = this.resolveAdminUserUrl(appUrl);
    if (adminUserUrl) {
      return adminUserUrl;
    }

    return notification.entityId
      ? `/superadmin/admins/${notification.entityId}`
      : "/superadmin/admins?status=PENDING";
  }

  private isAdminRegistrationNotification(notification: NotificationResponse): boolean {
    const type = notification.type?.toUpperCase() ?? "";
    const entityType = notification.entityType?.toUpperCase() ?? "";
    const text = `${notification.title ?? ""} ${notification.message ?? ""} ${notification.metadataJson ?? ""}`.toLowerCase();

    return type.includes("REGISTER") ||
      type.includes("ADMIN_SIGNUP") ||
      type.includes("PENDING_ACTIVATION") ||
      entityType.includes("ADMIN_USER") ||
      entityType.includes("ADMIN_REGISTRATION") ||
      text.includes("new admin") ||
      text.includes("registered") ||
      text.includes("registration") ||
      text.includes("pending activation");
  }

  private resolveAdminUserUrl(url: string): string {
    if (!url) {
      return "";
    }

    const normalized = url.toLowerCase();
    if (normalized === "/superadmin/admins" || normalized.startsWith("/superadmin/admins?")) {
      return url;
    }

    const superAdminDetailMatch = normalized.match(/^\/superadmin\/admins\/(\d+)/);
    if (superAdminDetailMatch?.[1]) {
      return `/superadmin/admins/${superAdminDetailMatch[1]}`;
    }

    const adminUsersMatch = normalized.match(/\/admin\/users\/admins\/(\d+)/);
    if (adminUsersMatch?.[1]) {
      return `/superadmin/admins/${adminUsersMatch[1]}`;
    }

    if (normalized.includes("/admin/users/admins")) {
      return "/superadmin/admins?status=PENDING";
    }

    return "";
  }

  private applyUserProfile(profile: UserProfile | null): void {
    if (!profile) {
      this.userDisplayName = this.getFallbackDisplayName();
      this.userProfileImage = "assets/images/profile/user-1.jpg";
      return;
    }

    const firstName = profile.firstName?.trim() || "";
    const lastName = profile.lastName?.trim() || "";
    const fullName = `${firstName} ${lastName}`.trim();

    this.userDisplayName = fullName || this.getFallbackDisplayName();
    this.userProfileImage =
      this.userService.getPreferredProfileImage(profile) || "assets/images/profile/user-1.jpg";
  }

  private getFallbackDisplayName(): string {
    if (!this.userEmail) {
      return this.userRoleLabel;
    }

    const emailName = this.userEmail.split("@")[0]?.trim();
    return emailName || this.userRoleLabel;
  }

  // ------------------------------
  // 🔵 Branding (logo) click
  // ------------------------------
  navigateToHome() {
    if (!this.isLoggedIn) {
      this.router.navigate(["/home"]);
      return;
    }
    this.router.navigate([this.getDashboardRoute(this.userRole)]);
  }

  // ------------------------------
  // 🔵 Back to Dashboard button
  // ------------------------------
  goBackToDashboard() {
    if (this.isLoggedIn) {
      this.router.navigate([this.getDashboardRoute(this.userRole)]);
    }
  }

  // ------------------------------
  // 🔵 Profile Navigation
  // ------------------------------
  goToProfile() {
    const profileRoutes: Record<string, string> = {
      admin: '/app/edit-profile',
      manager: '/app/edit-profile',
      sales: '/app/edit-profile',
      superadmin: '/superadmin/edit-profile',
      customer: '/customer/edit-profile',
    };

    this.router.navigate([profileRoutes[this.userRole] ?? '/home']);
  }

  goToSupport() {
    this.router.navigate(["/customer/contact-support"]);
  }

  goToCart() {
    this.router.navigate(["/customer/cart"]);
  }

  logout() {
    this.notificationInitialized = false;
    this.profileLoaded = false;
    this.notificationRealtime.stop();
    this.authService.logout("topstrip-user-action");
  }

  ngOnDestroy() {
    if (this.clockTimerId) {
      clearInterval(this.clockTimerId);
    }
    this.subs.unsubscribe();
  }

  onImageError(event: Event) {
    const img = event.target as HTMLImageElement;
    img.src = "assets/images/profile/user-1.jpg";
  }

  private logDev(message: string, detail?: unknown): void {
    if (!environment.production) {
      console.debug(message, detail ?? "");
    }
  }
}
